import Accordion from 'components/Accordion';
import Button from 'components/Button';
import Field from 'components/Form/Field';
import FormResult from 'components/Form/FormResult';
import KeyValue from 'components/Layout/KeyValue';
import SectionHeader from 'components/SectionHeader';
import Value from 'components/Value';
import {useCancelSecureSend} from 'pages/History/SendingOrders/Details/services';
import {SendNotificationEmail} from 'pages/History/SendingOrders/Details/types';
import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {t} from 'ttag';
import {onApiError} from 'utils/request-axios/utils';

import SentEmailTable from '../SentEmailTable';

type SendViaSecureSendStatusProps = {
    documentReminder?:
        | {
              message: null | string;
              sentEmailSet: SendNotificationEmail[];
              status: string;
              subject: null | string;
          }
        | undefined;
    isCancellable: boolean;
    isSelected: boolean;
    sentEmailSet?: SendNotificationEmail[];
    statusDescription: string;
    supersendMid: string;
};

const SendViaSecureSendStatus: FC<SendViaSecureSendStatusProps> = ({
    documentReminder,
    isCancellable,
    isSelected,
    sentEmailSet,
    statusDescription,
    supersendMid,
}) => {
    const methods = useForm();
    const apiCancelSecureSend = useCancelSecureSend();

    if (!isSelected) {
        return null;
    }

    const handleCancelSecureSend = async () => {
        await apiCancelSecureSend
            .mutateAsync({mid: supersendMid})
            .catch(onApiError(methods));
    };

    return (
        <FormProvider {...methods}>
            <div className="flex flex-col gap-4">
                <SectionHeader level={2} title={t`Secure Send`} />{' '}
                <Accordion
                    hasBorderWhenExpanded={false}
                    header={t`Status`}
                    isOpenByDefault={true}
                >
                    <div className="flex flex-col gap-4">
                        <div aria-label="Status for Secure Send">
                            {statusDescription}
                        </div>
                        {isCancellable && (
                            <>
                                <Field type="value">
                                    <Button
                                        kind="destructive"
                                        onClick={handleCancelSecureSend}
                                    >
                                        {t`Cancel Secure Send`}
                                    </Button>
                                </Field>
                                <FormResult />
                            </>
                        )}
                        {sentEmailSet && (
                            <SentEmailTable sentEmailSet={sentEmailSet} />
                        )}
                    </div>
                </Accordion>
                {documentReminder && (
                    <Accordion
                        hasBorderWhenExpanded={false}
                        header={t`Document Reminder`}
                        isOpenByDefault={false}
                    >
                        <div className="flex flex-col gap-4">
                            <KeyValue keyWidth={10}>
                                <Field label={t`Reminder Status`} type="value">
                                    <Value
                                        value={{
                                            type: 'string',
                                            value: documentReminder.status,
                                        }}
                                    />
                                </Field>
                                {documentReminder.subject && (
                                    <Field
                                        label={t`Reminder Email Subject`}
                                        type="value"
                                    >
                                        <Value
                                            value={{
                                                type: 'string',
                                                value: documentReminder.subject,
                                            }}
                                        />
                                    </Field>
                                )}
                                {documentReminder.message && (
                                    <Field
                                        label={t`Reminder Email Body`}
                                        type="value"
                                    >
                                        <Value
                                            value={{
                                                type: 'string',
                                                value: documentReminder.message,
                                            }}
                                        />
                                    </Field>
                                )}
                            </KeyValue>
                            {documentReminder.sentEmailSet.length ? (
                                <SentEmailTable
                                    sentEmailSet={documentReminder.sentEmailSet}
                                />
                            ) : null}
                        </div>
                    </Accordion>
                )}
            </div>
        </FormProvider>
    );
};
export default SendViaSecureSendStatus;
